import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import OrganizationLink from 'src/common/OrganizationLink';
import { IntegrationKeyType } from 'src/dashboard/integrations/list-factory';
import { Nullable } from 'src/types/nullable.type';
import { listedIntegrations } from 'src/utils/integration/providers';
import { apiGetUserIntegrations } from 'src/utils/journeyApi';
import { DashboardSettingsIntegrationsCard } from './card';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import { useParagon } from 'src/utils/use-paragon.hook';

export const DashbooardSettingsIntegrations = () => {
  const [loaded, setLoaded] = useState<Nullable<boolean>>(null);
  const integrationsMapRef = useRef<Map<string, boolean>>(new Map());

  const currentUser = useCurrentUser((state) => state.currentUser) as OrganizationUser;
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);

  const paragon = useParagon(currentUser, currentOrganization);

  useEffect(() => {
    const onDetectExtensionIsLoaded = () => {
      integrationsMapRef.current.set('chrome-extension', true);
    };

    document.addEventListener('extension:extension-loaded', onDetectExtensionIsLoaded);

    return () => {
      document.removeEventListener('extension:extension-loaded', onDetectExtensionIsLoaded);
    };
  }, []);

  useEffect(() => {
    const integrationMapSetter = (apiName: string) => {
      integrationsMapRef.current.set(apiName, true);
    };

    const fetchUserIntegrations = new Promise<void>((resolve) => {
      apiGetUserIntegrations().then(({ api_keys, tokens }) => {
        console.info({ api_keys, tokens });
        (api_keys as IntegrationKeyType[]).forEach((key) => integrationMapSetter(key.provider));
        (tokens as IntegrationKeyType[]).forEach((key) => integrationMapSetter(key.provider));
        document.dispatchEvent(new CustomEvent('extension:page-loaded'));
        resolve();
      });
    });

    const initializeParagon = async () => {
      const integrations = await paragon.initialize();
      integrations.forEach(integrationMapSetter);
    };

    if (loaded === null) {
      Promise.all([initializeParagon, fetchUserIntegrations]).then(() => {
        setLoaded(true);
      });
    }
  }, [loaded]);

  const availableIntegrations = listedIntegrations.filter((integration) => {
    const { feature_flag } = integration;
    if (feature_flag && !currentOrganization.feature_flags[feature_flag]) {
      return false;
    }

    return true;
  });

  return (
    <>
      <Helmet>
        <title>Journey | Integration Settings</title>
      </Helmet>
      <div className='flex flex-row flex-wrap gap-x-4 gap-y-6 mt-8'>
        {availableIntegrations.map((integration) => {
          const ChildElement = (
            <DashboardSettingsIntegrationsCard
              code={integration.code}
              name={integration.name}
              enabled={integrationsMapRef.current.has(integration.code)}
              enableCopy={integration.enableCopy}
              disableCopy={integration.disableCopy}
              description={integration.description}
            />
          );
          return (
            <>
              {integration.target ? (
                <a
                  className='border-r border-r-bedrock-gray-medium last:border-none hover:opacity-80'
                  href={integration.target}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {ChildElement}
                </a>
              ) : (
                <OrganizationLink
                  key={`integration-card-${integration.code}`}
                  to={`/integrations/${integration.code}`}
                  className='border-r border-r-bedrock-gray-medium last:border-none hover:opacity-80'
                >
                  {ChildElement}
                </OrganizationLink>
              )}
            </>
          );
        })}
      </div>
    </>
  );
};
